import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type MetricsService from 'garaje/services/metrics';

interface ViewProfileLinkSignature {
  Element: HTMLAnchorElement;
  Args: {
    visitorId: string;
    from: string;
    /**
     * The source record id, forwarded as the `id` query param for back
     * navigation. Named `sourceId` (not `id`) so two links on the same page —
     * e.g. "View Profile" + "View Identity document" — don't collide under the
     * template-lint `no-duplicate-id` rule, which treats an `@id` arg as an id.
     */
    sourceId: string;
    /**
     * Profile tab to deep-link to (e.g. 'identity'). Omitted → the profile
     * opens on its default tab. The tab is validated host-side (the show
     * controller clamps unknown/unauthorized tabs back to 'visits').
     */
    tab?: string;
    /** Link text. Defaults to "View Profile". */
    label?: string;
    /**
     * 'button' (default) renders the outline button; 'text' renders an inline
     * text link (the "View Identity document" affordance on invite/entry).
     */
    variant?: 'button' | 'text';
  };
}

export default class ViewProfileLink extends Component<ViewProfileLinkSignature> {
  @service declare metrics: MetricsService;

  get query(): { from: string; id: string; tab?: string } {
    const query: { from: string; id: string; tab?: string } = {
      from: this.args.from,
      id: this.args.sourceId,
    };
    // Only include `tab` when set — an empty `?tab=` would defeat the show
    // controller's default-tab behavior.
    if (this.args.tab) {
      query.tab = this.args.tab;
    }
    return query;
  }

  get linkClass(): string {
    return this.args.variant === 'text'
      ? 'view-profile-link--text text-gem-50 hover:underline'
      : 'btn btn--small btn--outlineFill';
  }

  trackViewProfileLinkClick = (): void => {
    this.metrics.trackEvent('Visitors Profiles View Profile Link Click', {
      visitor_profile_id: this.args.visitorId,
      from: this.args.from,
      id: this.args.sourceId,
      tab: this.args.tab ?? null,
    });
  };

  <template>
    <LinkTo
      ...attributes
      @route="visitors.profiles.show"
      @model={{@visitorId}}
      @query={{this.query}}
      {{on "click" this.trackViewProfileLinkClick}}
      class={{this.linkClass}}
      data-test-view-profile-link
    >
      {{if @label @label "View Profile"}}
    </LinkTo>
  </template>
}
